import { trackEvent, identifyUser } from '@offerzen/pinata-shame'

/**
 * Identifies candidate users, ignores those in onboarding.
 *
 * @param {Object} currentUser
 * @param {string} currentUser.id
 * @param {string} currentUser.name
 * @param {string} currentUser.email
 * @param {Date|string} currentUser.created_at
 * @param {Object} candidateProfile
 * @param {string} candidateProfile.currentState
 * @param {string} candidateProfile.slug
 */
export const identifyCandidate = async (currentUser, candidateProfile) => {
  const { currentState, slug } = candidateProfile

  if (currentState === 'ONBOARDING') {
    return
  }

  identifyUser(currentUser.id, {
    name: currentUser.name,
    email: currentUser.email,
    createdAt: currentUser.created_at,
    state: currentState,
    slug,
    traits: {
      user_type: 'candidate',
    },
    user_type: 'candidate',
  })
}

/**
 * Tracks when a candidate requests reactivation.
 *
 * @param {Object} params
 * @param {'candidate dashboard' | 'candidate profile' | 'candidate position details'} params.source
 * @param {string} params.candidateState
 * @param {string} [params.rejectReason=null] - If the candidate is rejected, the reason for the rejection.
 * @param {string} [params.positionId=null]
 * @param {string} [params.positionName=null]
 * @param {string} [params.companyName=null]
 * @param {string} [params.companyId=null]
 */
export const trackReactivate = ({
  source,
  candidateState,
  rejectReason = null,
  positionId = null,
  positionName = null,
  companyName = null,
  companyId = null,
}) => {
  trackEvent('Candidate: reapply', {
    action: 'submit reapply',
    source,
    candidateState,
    rejectReason,
    position_id: positionId,
    position_name: positionName,
    company_name: companyName,
    company_id: companyId,
  })
}

/**
 * Tracks when a candidate presses the button to update their profile or closes the modal after reactivating.
 *
 * @param {Object} params
 * @param {'update profile' | 'close modal'} params.action
 * @param {'candidate dashboard' | 'candidate profile' | 'candidate position details'} params.source
 * @param {string} params.candidateState
 */
export const trackActionAfterReactivate = ({ action, source, candidateState }) => {
  trackEvent('Candidate: reapply', {
    action,
    source,
    candidateState,
  })
}

/**
 * Triggers a GTM event when candidate submits their profile for review.
 * @param {Object} params
 * @param {string} params.source
 */
export const trackCandidateSubmitProfileForReviewEvent = ({ source }) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'Candidate Submit',
    action: 'submitted for review',
    label: 'offerzen core',
    source: 'reapply',
    source_detail: source,
  })
}

/**
 * Tracks when a candidate clicks a CTA to update their profile.
 *
 * @param {Object} params
 * @param {'candidate positions list' | 'candidate position details'} params.source
 * @param {string} params.candidateState
 * @param {string} [params.positionId=undefined] - Undefined if triggered from anywhere besides the job details page.
 * @param {string} [params.positionName=undefined] - Undefined if triggered from anywhere besides the job details page.
 * @param {string} [params.companyName=undefined] - Undefined if triggered from anywhere besides the job details page.
 * @param {string} [params.companyId=undefined] - Undefined if triggered from anywhere besides the job details page.
 */
export const trackUpdateProfile = ({ source, candidateState, positionId, positionName, companyName, companyId }) => {
  trackEvent('Candidate: profile action', {
    action: 'click update profile',
    source,
    candidate_state: candidateState,
    position_id: positionId,
    position_name: positionName,
    company_name: companyName,
    company_id: companyId,
  })
}
